import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Alert,
  MenuItem,
  Card,
  CardContent,
  CardActions,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  ContentCopy as CopyIcon,
  Visibility as PreviewIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import { apiUrl } from '../utils/api';
import LoadingOverlay from '../components/LoadingOverlay';

const DocumentTemplates = ({ access = 'none' }) => {
  const [templates, setTemplates] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [scopeType, setScopeType] = useState('global');
  const [formData, setFormData] = useState({
    template_name: '',
    template_type: 'invoice',
    template_content: '',
    header_template: '',
    footer_template: '',
    css_styles: '',
    variables: '',
    is_default: false,
    company_id: null
  });

  const canRead = access !== 'none';
  const canWrite = access === 'write' || access === 'admin';

  const templateTypes = [
    { value: 'invoice', label: 'Invoice' },
    { value: 'quotation', label: 'Quotation' },
    { value: 'contract', label: 'Contract' },
    { value: 'letter', label: 'Letter' },
    { value: 'report', label: 'Report' },
    { value: 'receipt', label: 'Receipt' },
    { value: 'certificate', label: 'Certificate' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    if (canRead) {
      fetchTemplates();
      fetchCompanies();
    }
  }, [canRead, selectedType]);

  const fetchCompanies = async () => {
    try {
      const response = await fetch(apiUrl('/api/companies'), {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setCompanies(data.companies || []);
      }
    } catch (err) {
      console.error('Failed to load companies:', err);
    }
  };

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedType) params.append('template_type', selectedType);

      const response = await fetch(apiUrl(`/api/templates?${params}`), {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      } else {
        throw new Error('Failed to fetch templates');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setScopeType(template.company_id ? 'company' : 'global');
      setFormData({
        template_name: template.template_name || '',
        template_type: template.template_type || 'invoice',
        template_content: template.template_content || '',
        header_template: template.header_template || '',
        footer_template: template.footer_template || '',
        css_styles: template.css_styles || '',
        variables: template.variables || '',
        is_default: template.is_default || false,
        company_id: template.company_id || null
      });
    } else {
      setEditingTemplate(null);
      setScopeType('global');
      setFormData({
        template_name: '',
        template_type: 'invoice',
        template_content: '',
        header_template: '',
        footer_template: '',
        css_styles: '',
        variables: '',
        is_default: false,
        company_id: null
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTemplate(null);
    setFormData({
      template_name: '',
      template_type: 'invoice',
      template_content: '',
      header_template: '',
      footer_template: '',
      css_styles: '',
      variables: '',
      is_default: false,
      company_id: null
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    if (saving) return;

    try {
      setSaving(true);
      const url = editingTemplate
        ? apiUrl(`/api/templates/${editingTemplate.template_id}`)
        : apiUrl('/api/templates');

      const response = await fetch(url, {
        method: editingTemplate ? 'PUT' : 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        await fetchTemplates();
        handleCloseDialog();
        setError(null);
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save template');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      const response = await fetch(apiUrl(`/api/templates/${templateId}`), {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        await fetchTemplates();
        setError(null);
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete template');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDuplicate = (template) => {
    setEditingTemplate(null);
    setScopeType(template.company_id ? 'company' : 'global');
    setFormData({
      template_name: `${template.template_name} (Copy)`,
      template_type: template.template_type,
      template_content: template.template_content || '',
      header_template: template.header_template || '',
      footer_template: template.footer_template || '',
      css_styles: template.css_styles || '',
      variables: template.variables || '',
      is_default: false,
      company_id: template.company_id || null
    });
    setOpenDialog(true);
  };

  const handlePreviewPDF = async (template) => {
    try {
      const response = await fetch(apiUrl(`/api/templates/${template.template_id}/preview`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ variables: {} })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to generate preview');
      }
    } catch (err) {
      setError('Error generating preview: ' + err.message);
    }
  };

  if (!canRead) {
    return (
      <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: 3 }}>
        <Alert severity="warning">
          You don't have permission to view document templates.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: 3, position: 'relative', minHeight: '500px' }}>
      <LoadingOverlay loading={loading} size={50} />

      <Box
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        mb={2}
      >
        {canWrite && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ px: 3, py: 1, borderRadius: 2 }}
          >
            New Template
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box mb={3}>
        <TextField
          select
          label="Filter by Type"
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          size="small"
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All Types</MenuItem>
          {templateTypes.map((type) => (
            <MenuItem key={type.value} value={type.value}>
              {type.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 3 }}>
        {templates.length === 0 && !loading ? (
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
              <DescriptionIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No templates found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {canWrite ? 'Create your first document template to get started' : 'No templates available'}
              </Typography>
            </Paper>
          </Box>
        ) : (
          templates.map((template) => (
            <Box key={template.template_id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                    <Typography variant="h6" fontWeight={600} noWrap>
                      {template.template_name}
                    </Typography>
                    {template.is_default && (
                      <Chip label="Default" color="success" size="small" />
                    )}
                  </Box>

                  <Box mb={2}>
                    <Chip
                      label={templateTypes.find(t => t.value === template.template_type)?.label || template.template_type}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {template.company_id ? 'Company-specific' : 'Global template'}
                  </Typography>

                  {template.variables && (
                    <Box mt={2}>
                      <Typography variant="caption" color="text.secondary">
                        Variables: {template.variables.split(',').length}
                      </Typography>
                    </Box>
                  )}
                </CardContent>

                <CardActions sx={{ p: 2, pt: 0, display: 'flex', justifyContent: 'space-between' }}>
                  <Box>
                    <Tooltip title="Preview PDF">
                      <IconButton
                        size="small"
                        onClick={() => handlePreviewPDF(template)}
                        color="secondary"
                      >
                        <PdfIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  {canWrite && (
                    <Box>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(template)}
                          color="primary"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Duplicate">
                        <IconButton
                          size="small"
                          onClick={() => handleDuplicate(template)}
                          color="primary"
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(template.template_id)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </CardActions>
              </Card>
            </Box>
          ))
        )}
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h5" fontWeight={600}>
            {editingTemplate ? 'Edit Template' : 'Create New Template'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="Template Name *"
                value={formData.template_name}
                onChange={(e) => handleInputChange('template_name', e.target.value)}
                required
                size="small"
              />
              <TextField
                select
                label="Template Type *"
                value={formData.template_type}
                onChange={(e) => handleInputChange('template_type', e.target.value)}
                required
                size="small"
              >
                {templateTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <TextField
              label="Template Content (HTML) *"
              value={formData.template_content}
              onChange={(e) => handleInputChange('template_content', e.target.value)}
              required
              multiline
              rows={8}
              size="small"
              placeholder="<div>Your template HTML here...</div>"
              helperText="Use {{variable_name}} for dynamic content"
            />

            <TextField
              label="Header Template (HTML)"
              value={formData.header_template}
              onChange={(e) => handleInputChange('header_template', e.target.value)}
              multiline
              rows={3}
              size="small"
              placeholder="<header>...</header>"
            />

            <TextField
              label="Footer Template (HTML)"
              value={formData.footer_template}
              onChange={(e) => handleInputChange('footer_template', e.target.value)}
              multiline
              rows={3}
              size="small"
              placeholder="<footer>...</footer>"
            />

            <TextField
              label="CSS Styles"
              value={formData.css_styles}
              onChange={(e) => handleInputChange('css_styles', e.target.value)}
              multiline
              rows={4}
              size="small"
              placeholder=".class { property: value; }"
            />

            <TextField
              label="Available Variables"
              value={formData.variables}
              onChange={(e) => handleInputChange('variables', e.target.value)}
              size="small"
              placeholder="company_name, invoice_number, date, amount"
              helperText="Comma-separated list of variables that can be used in this template"
            />

            <Box display="flex" alignItems="center" gap={2}>
              <TextField
                select
                label="Scope"
                value={scopeType}
                onChange={(e) => {
                  setScopeType(e.target.value);
                  if (e.target.value === 'global') {
                    handleInputChange('company_id', null);
                  }
                }}
                size="small"
                sx={{ flex: 1 }}
              >
                <MenuItem value="global">Global Template</MenuItem>
                <MenuItem value="company">Company-Specific</MenuItem>
              </TextField>

              {scopeType === 'company' && (
                <TextField
                  select
                  label="Select Company"
                  value={formData.company_id || ''}
                  onChange={(e) => handleInputChange('company_id', e.target.value || null)}
                  size="small"
                  required
                  sx={{ flex: 1 }}
                >
                  <MenuItem value="">
                    <em>Select a company...</em>
                  </MenuItem>
                  {companies.map((company) => (
                    <MenuItem key={company.company_id} value={company.company_id}>
                      {company.company_name}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              <Box>
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_default}
                    onChange={(e) => handleInputChange('is_default', e.target.checked)}
                  />
                  <Typography variant="body2" component="span" sx={{ ml: 1 }}>
                    Set as Default
                  </Typography>
                </label>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={handleCloseDialog} disabled={saving} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.template_name || !formData.template_type || !formData.template_content || saving}
            startIcon={saving ? <CircularProgress size={20} /> : null}
          >
            {saving ? 'Saving...' : editingTemplate ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentTemplates;
